/**
 * useProfiles — React Query hook for location profile CRUD.
 *
 * Requires authentication. All API calls include the auth token
 * via apiClient (which reads from authService).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { LocationProfile } from '../types';

interface ProfilesResponse {
  profiles: LocationProfile[];
  total: number;
}

interface CreateProfileInput {
  name: string;
  address: string;
  radiusKm: 2 | 5 | 10 | 15 | 20;
}

interface UpdateProfileInput {
  name?: string;
  address?: string;
  radiusKm?: 2 | 5 | 10 | 15 | 20;
}

export function useProfiles(isAuthenticated: boolean) {
  const queryClient = useQueryClient();

  const profilesQuery = useQuery({
    queryKey: ['profiles'],
    queryFn: () => apiClient.get<ProfilesResponse>('/api/profiles'),
    enabled: isAuthenticated,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateProfileInput) =>
      apiClient.post<LocationProfile>('/api/profiles', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProfileInput }) =>
      apiClient.put<LocationProfile>(`/api/profiles/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<void>(`/api/profiles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    },
  });

  return {
    profiles: profilesQuery.data?.profiles ?? [],
    total: profilesQuery.data?.total ?? 0,
    isLoading: profilesQuery.isLoading,
    error: profilesQuery.error,

    createProfile: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    updateProfile: (id: string, input: UpdateProfileInput) =>
      updateMutation.mutateAsync({ id, input }),
    isUpdating: updateMutation.isPending,

    deleteProfile: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,

    refetch: profilesQuery.refetch,
  };
}
